import { useState } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import Preloader from 'components/Preloader';
import { confirm } from 'components/MenuNew/ConfirmModal';
import { usePosCoupons } from 'pages/PosSettings/hooks/usePosCoupons';
import AddEditModal from 'pages/PosSettings/Coupons/AddEditModal';

const GRID_SX = {
  my: 2,
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: (theme) => theme.palette.primary.main,
    color: 'white',
  },
  '& .MuiDataGrid-row': {
    '&:nth-of-type(odd)': {
      backgroundColor: (theme) => theme.palette.action.hover,
    },
  },
  '& .MuiDataGrid-sortIcon': { color: 'white' },
  '& .MuiDataGrid-filterIcon': { color: 'white' },
  '& .MuiDataGrid-menuIcon': {
    '& .MuiButtonBase-root': { color: 'white' },
  },
};

const Coupons = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [currentRowData, setCurrentRowData] = useState(null);

  const {
    loading,
    activeCoupons,
    inactiveCoupons,
    productOptions,
    categoryOptions,
    buildColumns,
    saveCoupon,
    removeCoupon,
  } = usePosCoupons();

  const handleEdit = (rowData) => {
    setCurrentRowData(rowData);
    setShowModal(true);
  };

  const handleAdd = () => {
    setCurrentRowData(null);
    setShowModal(true);
  };

  const handleDelete = async (row) => {
    const confirmed = await confirm({
      title: 'confirm_modal.coupon',
      message: 'confirm_modal.are_you_sure_coupon',
      cancelText: 'confirm_modal.no',
      confirmText: 'confirm_modal.yes',
      confirmColor: 'primary',
      hideCloseIcon: true,
      onClose: () => {},
    });
    if (confirmed) {
      removeCoupon(row);
    }
  };

  const handleSubmit = (payload) =>
    // On failure the redux action already toasts, so the dialog stays open.
    saveCoupon(payload).then(
      () => setShowModal(false),
      () => {}
    );

  const columns = buildColumns(handleDelete, handleEdit);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {loading && <Preloader overlay={true} />}
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ mt: 2 }} variant="h6">
            {t('website_settings.active_coupons')}
          </Typography>
          <Button
            id="posCouponsAdd"
            onClick={handleAdd}
            variant="contained"
            color="primary"
            type="button"
          >
            <span className="icon icon-plus"></span>
            <span>{t('website_settings.add_coupon')}</span>
          </Button>
        </Stack>
        <Box sx={GRID_SX}>
          <DataGrid
            autoHeight={true}
            hideFooterPagination={true}
            hideFooterSelectedRowCount={true}
            isEditable={false}
            rows={activeCoupons}
            columns={columns}
          />
          <Typography sx={{ my: 2 }} variant="h6">
            {t('website_settings.inactive_expired_coupons')}
          </Typography>
          <DataGrid
            sx={{ mt: 2 }}
            autoHeight={true}
            hideFooterPagination={true}
            hideFooterSelectedRowCount={true}
            isEditable={false}
            rows={inactiveCoupons}
            columns={columns}
          />
        </Box>
        {showModal && (
          <AddEditModal
            loading={loading}
            data={currentRowData}
            productOptions={productOptions}
            categoryOptions={categoryOptions}
            showModal={showModal}
            onConfirm={handleSubmit}
            onCancel={() => setShowModal(false)}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default Coupons;
